import { useEffect, useState } from 'react';
import { getAssent } from '../services/api';

// The click-through agreement's version. Bumping it re-asks every user once.
//
// OBLIGATION (Terms s.1): any MATERIAL change to the Terms or Privacy Policy
// MUST bump this AND the "Last updated" dates on both pages — the bump is what
// re-asks every user to confirm, which the Terms now promise.
//
// EXCEPTION, founder decisions 2026-09-29 and 2026-10-06 (#2493, ADR 0034):
// the Privacy Policy's email-measurement and account-link change was NOT
// bumped. It went out as a dated notice on the Privacy Policy, effective the
// day it was published (2026-10-06, the founder waived the 30-day wait;
// enforced in code by TRACKING_STARTS_AT), instead of re-consent. Do not
// "fix" this by bumping. Material changes to the TERMS still bump.
//
// 2026-10-18 (#4727, founder "yes to both"): the refund rule (Terms s.7, ADR
// 0055: one rule for every paid product) and fair use (s.6) are material, so
// the version moved from '2026-08-16' and everyone is asked once. A student
// with an older record on file sees what changed (AssentGate, priorVersion).
export const ASSENT_VERSION = '2026-10-18';

// Broadcast so every consumer flips the moment the student agrees, without a
// reload. AssentGate lives inside AppLayout while ConciergeLauncher is mounted
// higher in the tree — they are siblings, not parent/child, so an event is the
// cheapest shared channel that doesn't mean threading a new provider through
// the whole app.
export const ASSENT_RECORDED_EVENT = 'stratia:assent-recorded';

export const assentBeltKey = (email) => `assent_${ASSENT_VERSION}_${email}`;

/**
 * Has this student accepted the current Terms/Privacy version?
 *
 * Returns one of:
 *   'unknown'  — still resolving. Render neither the gate nor anything the
 *                gate is supposed to precede.
 *   'needed'   — a definitive "no record for this version". Only this value
 *                opens the modal.
 *   'assented' — on file (or the belt says so). The ONLY value that unlocks
 *                a surface gated on assent — Astra, the tour. Deliberately
 *                strict: nothing here may fail OPEN. A definite "no record"
 *                got its own value so an unread/unreadable result can never
 *                be confused with a real yes.
 *   'error'    — the read failed. The MODAL treats this like 'assented'
 *                (never trap an already-agreed student behind a dialog
 *                because of our 500 — the modal is the enforcement layer,
 *                a rare re-ask is the acceptable cost of a read blip). The
 *                GATED SURFACES must NOT make that same trade: they were
 *                built to fail closed. A single shared "fail open" value
 *                collapsed both meanings into one and let Astra through for
 *                a brand-new, never-assented student on any read hiccup —
 *                caught live 2026-08-18. Each consumer now reads the value
 *                that matches what it's protecting: AssentGate checks
 *                `status !== 'needed'`, gated surfaces check
 *                `status === 'assented'` exactly.
 *
 * The localStorage belt keeps returning students off the network entirely;
 * clearing it re-asks the backend, never the student.
 *
 * Takes the email rather than reading a context: callers disagree about where
 * identity comes from (AssentGate uses AuthContext, ConciergeLauncher reads
 * firebase auth directly), and a hook that picked one would break the other.
 */
export default function useAssentStatus(email) {
    return useAssentDetail(email).status;
}

/**
 * The status above, plus `priorVersion`: the version of an older agreement on
 * file when the answer is 'needed' because the Terms changed since (a
 * re-ask), or null for a student who never agreed. AssentGate reads it to say
 * what changed; nothing else needs it.
 */
export function useAssentDetail(email) {
    const [status, setStatus] = useState('unknown');
    const [priorVersion, setPriorVersion] = useState(null);

    useEffect(() => {
        setPriorVersion(null);
        if (!email) { setStatus('unknown'); return undefined; }

        let stale = false;
        const markAssented = () => { if (!stale) setStatus('assented'); };
        window.addEventListener(ASSENT_RECORDED_EVENT, markAssented);
        const unsubscribe = () => {
            stale = true;
            window.removeEventListener(ASSENT_RECORDED_EVENT, markAssented);
        };

        // The belt short-circuits the NETWORK, not just the state: a returning
        // student must not pay a fetch on every page load. Reading it and then
        // asking anyway would defeat the only reason it exists.
        try {
            if (localStorage.getItem(assentBeltKey(email))) {
                setStatus('assented');
                return unsubscribe;
            }
        } catch { /* storage unavailable — fall through to the backend */ }

        getAssent().then((res) => {
            if (stale) return;
            if (res?.success !== true) { setStatus('error'); return; }
            if (res.assent?.version === ASSENT_VERSION) {
                try { localStorage.setItem(assentBeltKey(email), 'true'); } catch { /* belt only */ }
                setStatus('assented');
            } else {
                setPriorVersion(res.assent?.version ? String(res.assent.version) : null);
                setStatus('needed');
            }
        }).catch(() => { if (!stale) setStatus('error'); });

        return unsubscribe;
    }, [email]);

    return { status, priorVersion };
}
